import React from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SmsIcon from '@mui/icons-material/Sms';
import Store from '../../utilities/store';
import RequestManager from '../../utilities/requestManager';
import { StateManager } from '../../utilities/stateManager';

const BLUE = '#023e84';
const TRACKER_BASE_URL = 'https://skywayclassics.com/title';

const when = (iso) => (iso ? moment(iso).format('MMM D, h:mm a') : null);

// The buyer's link to the agora title tracker (/title/[token]). The buyer
// unlocks it with the last 4 digits of their phone. The backend makes the
// token; this panel copies or texts it and shows whether the buyer has looked.
export default function TitleTrackerPanel({ stockNumber, hasBuyer }) {
  const [tracker, setTracker] = React.useState(null);
  const [busy, setBusy] = React.useState(null);

  React.useEffect(() => {
    let live = true;
    if (!stockNumber) return undefined;
    Store.getTitleTracker(stockNumber)
      .then((t) => { if (live) setTracker(t); })
      .catch((e) => StateManager.setAlertAndOpen('Could not load the title tracker: ' + e.message, 'error'));
    return () => { live = false; };
  }, [stockNumber]);

  const run = async (sendText) => {
    setBusy(sendText ? 'text' : 'copy');
    try {
      const res = await RequestManager.post({
        function: 'createTitleTracker',
        variables: { stockNumber, sendText },
      });
      if (!res.success) throw new Error(res.message || 'Request failed');
      if (sendText) {
        StateManager.setAlertAndOpen(`Tracker link texted to ${res.textedTo}`, 'success');
      } else {
        await navigator.clipboard.writeText(res.url);
        StateManager.setAlertAndOpen('Tracker link copied', 'success');
      }
      setTracker(await Store.getTitleTracker(stockNumber));
    } catch (e) {
      StateManager.setAlertAndOpen(e.message || 'Title tracker error', 'error');
    } finally {
      setBusy(null);
    }
  };

  if (!hasBuyer) {
    return (
      <Typography variant="body2" color="text.secondary">
        Add a buyer to the deal to create a title tracker link.
      </Typography>
    );
  }

  const views = (tracker && tracker.view_count) || 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography variant="body2" color="text.secondary">
        The buyer sees their title stage on skywayclassics.com. They unlock it with the last 4 digits of their phone.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={busy === 'copy' ? <CircularProgress size={12} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
          disabled={!!busy}
          onClick={() => run(false)}
          sx={{ borderColor: BLUE, color: BLUE }}
        >
          Copy Link
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={busy === 'text' ? <CircularProgress size={12} /> : <SmsIcon sx={{ fontSize: 14 }} />}
          disabled={!!busy}
          onClick={() => run(true)}
          sx={{ borderColor: BLUE, color: BLUE }}
        >
          Text Link to Buyer
        </Button>
      </Box>
      {tracker && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {`${TRACKER_BASE_URL}/${tracker.token}`}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: views ? 'success.dark' : 'text.secondary' }}>
            {views
              ? `Buyer viewed ${views} time${views === 1 ? '' : 's'} · last ${when(tracker.last_viewed_at)}`
              : 'Buyer has not opened it yet'}
          </Typography>
          {tracker.last_texted_at && (
            <Typography variant="caption" color="text.secondary">
              {`Last texted ${when(tracker.last_texted_at)} to ${tracker.last_texted_to}`}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
